import type { TChatAttachment, TLocalChatMessage, TUserResponse } from '@chain-zen/chat'

import type { IChatUser } from '../types/chat-types'

export const normalizeChatUser = (user: IChatUser | undefined) => {
  if (!user) return null
  return {
    email: user.email,
    id: user.id,
    isVerified: user?.is_verified,
    name: user?.name || '',
    online: user?.online,
    phone: user.phone,
    photo: user.image,
    role: user.user_role,
    username: user?.username || ''
  }
}

export const SUPPORTED_VIDEO_FORMATS = ['video/mp4', 'video/ogg', 'video/webm', 'video/quicktime']

export const isTiffAttachment = (attachment: TChatAttachment): boolean =>
  attachment.type === 'image' && attachment.mime_type === 'image/tiff'

export const isImageAttachment = (attachment: TChatAttachment): boolean =>
  attachment.type === 'image' && !attachment.title_link && !attachment.og_scrape_url

export const isAudioAttachment = (attachment: TChatAttachment): boolean => attachment.type === 'audio'

export const isFileAttachment = (attachment: TChatAttachment) =>
  attachment.type === 'file' ||
  (attachment.mime_type &&
    SUPPORTED_VIDEO_FORMATS.indexOf(attachment.mime_type) === -1 &&
    attachment.type !== 'video' &&
    attachment.type !== 'audio')

export const isVideoAttachment = (attachment: TChatAttachment) =>
  (attachment.mime_type && SUPPORTED_VIDEO_FORMATS.indexOf(attachment.mime_type) !== -1) || attachment.type === 'video'

export const isGifAttachment = (attachment: TChatAttachment): boolean =>
  attachment.type === 'image' && Boolean(attachment.image_url)

export const isVideoLinkAttachment = (attachment: TChatAttachment): boolean =>
  (attachment.mime_type && SUPPORTED_VIDEO_FORMATS.indexOf(attachment.mime_type) !== -1) ||
  (attachment.type === 'video' && !!attachment.author_name)

export const formatMessageForPreview = (lastMessage: TLocalChatMessage | undefined) => {
  let lastText = 'Send a message...'
  if (lastMessage) {
    if (lastMessage?.attachments?.length) {
      const attachment = lastMessage.attachments[0]
      let attachmentType = ''

      if (attachment.title_link) lastText = `Link: ${attachment.title_link}`
      switch (true) {
        case isAudioAttachment(attachment):
          attachmentType = 'Audio'
          break
        case isFileAttachment(attachment):
          attachmentType = 'File'
          break
        case isGifAttachment(attachment):
          attachmentType = 'Gif'
          break
        case isImageAttachment(attachment):
          attachmentType = 'Image'
          break
        case isVideoLinkAttachment(attachment):
          attachmentType = 'Video Link'
          break
        case isVideoAttachment(attachment):
          attachmentType = 'Video'
          break
      }
      if (attachmentType)
        lastText = `${attachmentType}: ${attachment.fallback || attachment.title || attachment.image_url}`
    } else lastText = `${lastMessage?.text}`
  }
  return lastText
}

/**
 * Returns a string indicating the file type or link
 *
 * @param lastMessage last message in the chat channel
 * @param myUserID userID of the logged-in user
 * @returns string
 */
export const formatMessageForPreviewWithSenderName = (
  lastMessage: TLocalChatMessage | undefined,
  myUserID: string | undefined
) => {
  let lastText = 'Send a message...'
  if (lastMessage) {
    let sentBy = ''

    const actionBy =
      (lastMessage.initiated_by &&
        lastMessage?.mentioned_users?.find((u: TUserResponse) => u.id == lastMessage.initiated_by)) ||
      lastMessage?.user

    if (lastMessage?.initiated_by === myUserID || lastMessage?.user?.id === myUserID) sentBy = 'You: '
    else if (actionBy) {
      const user = actionBy as TUserResponse & { email?: string; phone?: string }
      sentBy = `${user?.name || user?.email || user?.phone || ''}: `
    }
    if (lastMessage?.attachments?.length) {
      const attachment = lastMessage.attachments[0]
      let attachmentType = ''

      if (attachment.title_link) lastText = sentBy + `Link:${attachment.title_link}`
      switch (true) {
        case isAudioAttachment(attachment):
          attachmentType = 'Audio'
          break
        case isFileAttachment(attachment):
          attachmentType = 'File'
          break
        case isGifAttachment(attachment):
          attachmentType = 'Gif'
          break
        case isImageAttachment(attachment):
          attachmentType = 'Image'
          break
        case isVideoLinkAttachment(attachment):
          attachmentType = 'Video Link'
          break
        case isVideoAttachment(attachment):
          attachmentType = 'Video'
          break
      }
      if (attachmentType) lastText = sentBy + `${attachmentType}:${attachment.fallback || attachment.title}`
    } else lastText = sentBy + `${lastMessage?.text}`
  }
  return lastText
}
